import { useContext, useState } from "react";
import { Context } from "../../../../context/Context";
import { Button, NoticeBox } from "../../../ui";
import { getBillingReconciliationAPI } from "../../../../utils/api/billing";

/*
  Read-only review of the billing estate: legacy paying branches without coverage, subscription / invoice / payment inconsistencies and the
  decisions they need. It changes nothing; every fix is a separate, reviewed administrative action. Loaded when opened (global administrators
  only: the server answers 403 to everybody else).
*/
const TEXT = {
  ar: {
    title: "مراجعة اتساق الفوترة والعملاء القدامى",
    refresh: "تحديث",
    readOnly: "للقراءة فقط: لا تُغيَّر أي بيانات ولا تُنشأ اشتراكات أو تغطية تلقائيًا.",
    denied: "هذه المراجعة متاحة للإدارة العليا فقط.",
    failed: "تعذر تحميل المراجعة",
    totals: "الإجماليات",
    branches: "تصنيف الفروع التشغيلية",
    findings: "الملاحظات",
    none: "لا توجد ملاحظات.",
    legacy: "فروع عملاء قدامى بدون تغطية",
    store: "المتجر",
    branchCount: "الفروع",
    invoices: "الفواتير",
    payments: "المدفوعات",
    decisions: "قرارات إدارية مطلوبة",
    classes: {
      active_platform_coverage: "مغطّاة باشتراك فعّال",
      subscription_not_current: "اشتراك غير ساري",
      legacy_entitlement_no_coverage: "عميل قديم بدون تغطية",
      draft_or_inactive: "مسودة أو متجر غير فعّال",
      missing_or_ambiguous: "غير محدد",
    },
  },
  en: {
    title: "Billing consistency and legacy customers review",
    refresh: "Refresh",
    readOnly: "Read-only: nothing is changed and no subscription or coverage is created automatically.",
    denied: "This review is for the top administrators only.",
    failed: "Unable to load the review",
    totals: "Totals",
    branches: "Operational branches by class",
    findings: "Findings",
    none: "No findings.",
    legacy: "Legacy customers' branches without coverage",
    store: "Store",
    branchCount: "Branches",
    invoices: "Invoices",
    payments: "Payments",
    decisions: "Administrative decisions needed",
    classes: {
      active_platform_coverage: "Covered by an active subscription",
      subscription_not_current: "Subscription not current",
      legacy_entitlement_no_coverage: "Legacy customer, no coverage",
      draft_or_inactive: "Draft or inactive store",
      missing_or_ambiguous: "Missing or ambiguous",
    },
  },
};

export default function ReconciliationPanel() {
  const { lang, handleTokenExpiration } = useContext(Context);
  const t = TEXT[lang === "ar" ? "ar" : "en"];
  const [state, setState] = useState({ status: "idle", data: null });

  const load = async () => {
    const token = localStorage.getItem("auth-token");
    if (!token) return handleTokenExpiration();
    setState((previous) => ({ ...previous, status: "loading" }));
    try {
      const result = await getBillingReconciliationAPI(token);
      setState({ status: "ready", data: result.data });
    } catch (error) {
      setState({ status: error.code === "BILLING_ACCESS_DENIED" ? "denied" : "error", data: null });
    }
  };

  const data = state.data;
  const findings = data
    ? [["subscriptions", data.subscriptions.counts], ["invoices", data.invoices.counts], ["payments", data.payments.counts]].flatMap(([section, counts]) =>
        Object.entries(counts).map(([code, count]) => ({ section, code, count })),
      )
    : [];

  return (
    <section className="billing-reconciliation" dir={lang === "ar" ? "rtl" : "ltr"}>
      <details onToggle={(event) => event.currentTarget.open && state.status === "idle" && load()}>
        <summary>{t.title}</summary>
        <NoticeBox variant="info" size="sm">{t.readOnly}</NoticeBox>
        {state.status === "loading" && <p role="status">…</p>}
        {state.status === "denied" && <NoticeBox variant="warning">{t.denied}</NoticeBox>}
        {state.status === "error" && (
          <div role="alert">
            <NoticeBox variant="danger" title={t.failed}>
              <Button size="sm" appearance="soft" onClick={load}>{t.refresh}</Button>
            </NoticeBox>
          </div>
        )}
        {state.status === "ready" && data && (
          <div className="billing-reconciliation-body">
            <p>
              {t.totals}: {Object.entries(data.totals).map(([key, value]) => `${key} ${value}`).join(" · ")}
            </p>
            <h4>{t.branches}</h4>
            <ul>
              {Object.entries(data.branches.byClass).map(([key, value]) => (
                <li key={key}>{t.classes[key] || key}: <strong>{value}</strong></li>
              ))}
            </ul>
            <h4>{t.findings}</h4>
            {!findings.length ? (
              <p>{t.none}</p>
            ) : (
              <ul>
                {findings.map((row) => (
                  <li key={`${row.section}-${row.code}`} dir="ltr">{row.section} / {row.code}: <strong>{row.count}</strong></li>
                ))}
              </ul>
            )}
            {data.legacyPayingBranches.length > 0 && (
              <>
                <h4>{t.legacy}</h4>
                <div className="cb-table-scroll">
                  <table>
                    <thead>
                      <tr><th>{t.store}</th><th>{t.branchCount}</th><th>{t.invoices}</th><th>{t.payments}</th></tr>
                    </thead>
                    <tbody>
                      {data.legacyPayingBranches.map((row) => (
                        <tr key={row.store}><td dir="ltr">{row.store}</td><td>{row.branches}</td><td>{row.invoices}</td><td>{row.succeededPayments}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            {data.decisions.length > 0 && (
              <>
                <h4>{t.decisions}</h4>
                <ul>
                  {data.decisions.map((decision) => (
                    <li key={decision.code}><strong dir="ltr">{decision.code}</strong> ({decision.count}): {decision.decision}</li>
                  ))}
                </ul>
              </>
            )}
            <Button size="sm" appearance="soft" onClick={load}>{t.refresh}</Button>
          </div>
        )}
      </details>
    </section>
  );
}
